import { DownOutlined } from '@ant-design/icons';
import { Empty, Spin, Tree, TreeDataNode } from 'antd';
import { DirectoryTreeProps } from 'antd/es/tree';
import { Key, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CategoryType } from '../../../../../lib/global-type';
import { axiosInstance } from '../services/axios-instance';
import { AppDispatch, RootState } from '../store';
import { setterSharingDocument } from '../store/sharing-document-slice';
import { CustomersResponseType, DocumentTreeResponseType } from '../types/types';

export type TreeNodeWithMetadataType = TreeDataNode & {
    metadata?: CategoryType | null;
    customer?: string;
};

const { DirectoryTree } = Tree;

export const DirectoryTreeWrapper = () => {
    const dispatch = useDispatch<AppDispatch>();
    const selectedTreeKey = useSelector((state: RootState) => state.sharingDocument.selectedTreeKey);
    const documentTree = useSelector((state: RootState) => state.sharingDocument.documentTree);

    const { user_sub } = useParams<{ user_sub: string }>();
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

    // Find all parent keys for a given node
    const findPathToNode = (targetKey: string, nodes: TreeNodeWithMetadataType[], path: Key[] = []): Key[] | null => {
        for (const node of nodes) {
            const currentPath = [...path, node.key];

            if (node.key === targetKey) {
                return currentPath.slice(0, -1); // Return all keys except the target itself
            }

            if (node.children && node.children.length > 0) {
                const found = findPathToNode(targetKey, node.children as TreeNodeWithMetadataType[], currentPath);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    // Expand tree when selectedTreeKey changes
    useEffect(() => {
        if (selectedTreeKey.length > 0 && documentTree.length > 0) {
            const targetKey = selectedTreeKey[0];
            const pathToNode = findPathToNode(targetKey, documentTree);

            if (pathToNode && pathToNode.length > 0) {
                setExpandedKeys((prev) => {
                    const newKeys = [...new Set([...prev, ...pathToNode])];
                    return newKeys;
                });
            }
        }
    }, [selectedTreeKey, documentTree]);
    const [loadingDocumentTree, setLoadingDocumentTree] = useState(false);

    useEffect(() => {
        const fetchDocumentTree = async () => {
            if (!user_sub) return;

            setLoadingDocumentTree(true);
            try {
                // Fetch customers list
                const customersResponse = await axiosInstance.get<CustomersResponseType>('/customers');

                // Filter customers by contractor (user_sub)
                const userCustomers = customersResponse.data.customers.filter(
                    (customer) => customer.contractor === user_sub,
                );

                dispatch(setterSharingDocument({ listCustomer: userCustomers }));

                if (!userCustomers.length) {
                    dispatch(setterSharingDocument({ documentTree: [] }));
                    return;
                }

                // Fetch document tree for each customer
                const treePromises = userCustomers.map(async (customer, index) => {
                    try {
                        const treeResponse = await axiosInstance.get<DocumentTreeResponseType>('/document-tree', {
                            params: { customer: customer.customer },
                        });

                        if (!index) {
                            dispatch(setterSharingDocument({ selectedTreeKey: [`customer-${customer.customer}`] }));
                        }

                        // Create customer root node
                        const customerNode: TreeNodeWithMetadataType = {
                            key: `customer-${customer.customer}`,
                            title: customer.customer,
                            customer: customer.customer,
                            children: treeResponse.data.documentTree[0].children || [],
                        };

                        return customerNode;
                    } catch (error) {
                        console.error(`Failed to fetch tree for ${customer.customer}:`, error);
                        return null;
                    }
                });

                const trees = await Promise.all(treePromises);
                const validTrees = trees.filter(Boolean) as TreeNodeWithMetadataType[];

                const getAllKeysFromTree = (nodes: TreeNodeWithMetadataType[]): string[] => {
                    let keys: string[] = [];
                    nodes.forEach((node) => {
                        keys.push(`${node.key}`);
                        if (node.children && node.children.length > 0) {
                            keys = [...keys, ...getAllKeysFromTree(node.children as TreeNodeWithMetadataType[])];
                        }
                    });
                    return keys;
                };

                const allKeys = getAllKeysFromTree(validTrees);

                dispatch(setterSharingDocument({ documentTree: validTrees, allTreeKeys: allKeys }));
            } catch (error) {
                console.error('Failed to fetch document tree:', error);
            } finally {
                setLoadingDocumentTree(false);
            }
        };

        fetchDocumentTree();
    }, [user_sub]);

    const onSelect: DirectoryTreeProps['onSelect'] = (selectedKeys, info) => {
        console.log('Selected Keys:', selectedKeys);
        console.log('------', info);

        dispatch(setterSharingDocument({ selectedTreeKey: selectedKeys as string[] }));
    };

    return (
        <Spin spinning={loadingDocumentTree} tip={'Loading...'}>
            {documentTree.length ? (
                <DirectoryTree
                    showLine
                    blockNode
                    titleRender={(nodeData) => {
                        return <span data-node-key={nodeData.key}>{nodeData.title as string}</span>;
                    }}
                    treeData={documentTree}
                    expandedKeys={expandedKeys}
                    selectedKeys={selectedTreeKey}
                    switcherIcon={<DownOutlined />}
                    style={{ whiteSpace: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}
                    onSelect={onSelect}
                    onExpand={(s) => {
                        console.log('===========');
                        setExpandedKeys(s);
                    }}
                />
            ) : (
                <Empty />
            )}
        </Spin>
    );
};
